import React from "react";
import {Link} from "react-router-dom";
import {ArrowRight, ArrowUpRight, CalendarDays, Compass, Layers3, ShieldCheck, Sparkles, Stethoscope, Workflow} from "lucide-react";
import PageMeta from "../../components/seo/PageMeta";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";

const SHIFTBRIDGE_LINK = "https://shifts.tritalcare.com/";
const products=[
  {
    title:"TRITAL ShiftBridge™",subtitle:"PRN Workforce Marketplace",
    description:"Explore PRN and per-diem shifts, clinician onboarding, facility access, and opportunities as they become available.",
    Icon:CalendarDays,status:"Marketplace preview",path:SHIFTBRIDGE_LINK,live:true,button:"Explore ShiftBridge",
    details:["Find PRN shifts","Clinician and facility portals","Credentials and scheduling workflows"]
  },
  {
    title:"TRITAL Workforce OS™",subtitle:"Recruiter Intelligence & RateLab",
    description:"Access RateLab V9, recruiting intelligence, resume tools, and competency workflows. These tools are now part of Platforms—not a separate corporate navigation section.",
    Icon:Layers3,status:"Recruiter tools",path:"/workforce-os",live:true,button:"Open Workforce OS",
    details:["RateLab V9 pay-package intelligence","Resume and submission-readiness workflows","Recruiter tools under one platform"]
  },
  {
    title:"TRITAL Orbit™",subtitle:"Workforce Intelligence Platform",
    description:"Open the dedicated TRITAL Orbit workforce mobility application. Additional account and backend services are being activated separately.",
    Icon:Compass,status:"Web app available",path:"https://orbit.tritalcare.com/",live:true,button:"Open Orbit",
    details:["Dedicated platform website","Separate deployment","Production subdomain connected"]
  },
  {
    title:"TRITAL Nexus™",subtitle:"Connected Workforce Operations",
    description:"Open the dedicated TRITAL Nexus recruiter operating system. Additional account and backend services are being activated separately.",
    Icon:Workflow,status:"Web app available",path:"https://nexus.tritalcare.com/",live:true,button:"Open Nexus",
    details:["Dedicated platform website","Separate deployment","Production subdomain connected"]
  }
];

export default function Platforms(){
 return <div className="min-h-screen bg-white" data-testid="platforms-page">
  <PageMeta title="TRITAL Platforms | ShiftBridge, Orbit, Nexus & Workforce OS"
   description="Discover TRITAL Care platforms for PRN shifts, healthcare staffing operations, workforce intelligence and recruiter tools."
   canonical="https://www.tritalcare.com/platforms"/>
  <PublicNav/>
  <main>
   <section className="relative overflow-hidden bg-[#f7f9fe] border-b border-slate-200">
    <div className="absolute inset-0 bg-trital-mesh opacity-70 pointer-events-none"/>
    <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
     <div className="inline-flex items-center gap-2 text-trital-navy text-xs font-extrabold tracking-[0.18em] uppercase">
      <Layers3 size={17} className="text-trital-red"/> The TRITAL Ecosystem
     </div>
     <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight font-black text-[#081f4f] mt-5">
      Connected workforce.<br/><span className="text-trital-red">Purpose-built platforms.</span>
     </h1>
     <p className="max-w-[660px] mt-6 text-[16px] text-slate-600 leading-relaxed">
      The original staffing services remain under Services and Solutions. ShiftBridge, Workforce OS, Orbit and Nexus are organized here as TRITAL Care platforms.
     </p>
    </div>
   </section>
   <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12 lg:py-16">
    <div className="grid md:grid-cols-2 gap-5">
     {products.map((p,i)=><article key={p.title} className={"rounded-[26px] bg-white border p-7 lg:p-8 flex flex-col transition-shadow "+(p.live?"border-[#cfdaed] hover:shadow-xl":"border-slate-200")} data-testid={"platform-card-"+i}>
       <div className="flex items-start justify-between gap-3">
        <div className={"w-14 h-14 rounded-2xl grid place-items-center "+(p.live?"bg-[#eaf0ff] text-trital-navy":"bg-slate-100 text-[#395477]")}><p.Icon size={26} strokeWidth={1.65}/></div>
        <span className={"rounded-full text-[11px] font-bold px-3 py-1.5 "+(p.live?"bg-emerald-50 text-emerald-800":"bg-slate-100 text-slate-600")}>{p.live?"Explore platform":"Coming soon"}</span>
       </div>
       <div className="mt-8 text-xs text-trital-red font-bold tracking-widest uppercase">{p.subtitle}</div>
       <h2 className="font-display mt-2 text-[28px] text-[#0e275b] font-black">{p.title}</h2>
       <p className="text-slate-600 leading-relaxed text-sm mt-3 flex-1">{p.description}</p>
       <ul className="mt-7 space-y-3">
        {p.details.map(item=><li className="text-sm text-[#394963] flex gap-2 items-center" key={item}><ShieldCheck size={15} className="text-[#1b50a5] shrink-0"/>{item}</li>)}
       </ul>
       {p.live?<a href={p.path} target={p.path.startsWith("https://") ? "_blank" : undefined} rel={p.path.startsWith("https://") ? "noopener noreferrer" : undefined} className="mt-8 flex justify-center gap-2 items-center px-5 py-3.5 rounded-xl text-white bg-trital-navy font-bold text-sm hover:bg-trital-navy-deep" data-testid={p.button==="Explore ShiftBridge"?"platform-shiftbridge-link":"platform-workforce-os-link"}>{p.button} <ArrowRight size={16}/></a>:
        <div className="mt-8 rounded-xl bg-[#f5f7fb] border border-slate-200 p-3.5" aria-label={p.title+" launch status"}>
         <span className="text-[11px] text-slate-500 font-semibold">Planned platform address</span>
         <div className="font-bold text-[13px] text-[#142e5d] mt-1 break-all">{p.domain}</div>
         <Link to={"/platforms/"+(p.title.includes("Orbit")?"orbit":"nexus")} className="mt-3 inline-flex items-center gap-2 text-trital-navy text-xs font-black hover:text-trital-red">View coming-soon page <ArrowRight size={14}/></Link>
         <p className="text-xs text-slate-500 mt-1.5">External domain link activates after deployment and DNS validation.</p>
        </div>}
      </article>)}
    </div>
   </section>

  </main>
  <PublicFooter/>
 </div>;
}
